import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { occurrenceLabelValues } from '@/features/planned/paycheckParts';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { InfoIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import type { RegisterRow } from '@/api/projections';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import type { DrillDown } from './types';

interface PlanRegisterProps {
  rows: RegisterRow[];
  currency: string | null;
  /** Omit in a read-only surface (session looking-ahead). */
  onDrill?: (drill: DrillDown) => void;
}

function description(row: RegisterRow): string {
  if (row.kind === 'PLANNED_EXPENSE') return row.note ?? row.envelopeName ?? row.payee ?? '';
  return row.payee ?? row.envelopeName ?? row.note ?? '';
}

export function PlanRegister({ rows, currency, onDrill }: PlanRegisterProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const xs = useMediaQuery(theme.breakpoints.down('md'));
  const opening = rows.find((r) => r.kind === 'OPENING');
  const rest = rows.filter((r) => r.kind !== 'OPENING');
  const todayBalance = opening?.balanceMinor ?? opening?.runningBalanceMinor ?? null;

  function drillRow(row: RegisterRow) {
    if (onDrill === undefined) return;
    const date = row.occursOn ?? row.date;
    onDrill({
      from: date,
      to: date,
      highlight: {
        date,
        ...(row.sliceRef !== null ? { sliceRef: row.sliceRef } : {}),
        ...(row.seriesId !== null ? { seriesId: row.seriesId } : {}),
      },
    });
  }

  return (
    <Table size="small" aria-label={t('cashflow.register')} sx={{ mb: 3 }}>
      <TableHead>
        <TableRow>
          <TableCell>{t('cashflow.colDate')}</TableCell>
          <TableCell>{t('cashflow.colDescription')}</TableCell>
          {!xs && <TableCell align="right">{t('cashflow.colAmount')}</TableCell>}
          <TableCell align="right">{t('cashflow.colRunning')}</TableCell>
          {!xs && <TableCell>{t('cashflow.colState')}</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow
          hover={onDrill !== undefined}
          onClick={onDrill && opening ? () => drillRow(opening) : undefined}
          sx={{ cursor: onDrill && opening ? 'pointer' : 'default' }}
        >
          <TableCell>
            <Typography variant="body2">{t('cashflow.todayRow')}</Typography>
          </TableCell>
          <TableCell />
          {!xs && <TableCell />}
          <TableCell align="right">
            <MoneyText minor={todayBalance} currency={currency} />
          </TableCell>
          {!xs && <TableCell />}
        </TableRow>
        {rest.map((row, index) => {
          const differs = row.differsFromSeries;
          const exception = differs !== null && (differs.date || differs.amount || differs.slices);
          const running = row.runningBalanceMinor;
          return (
            <TableRow
              key={`${row.kind}-${row.date}-${row.seriesId ?? ''}-${row.sliceRef ?? index}`}
              hover={onDrill !== undefined}
              onClick={onDrill ? () => drillRow(row) : undefined}
              sx={{ cursor: onDrill ? 'pointer' : 'default' }}
            >
              <TableCell>
                <Typography variant="body2">
                  {formatCivilDate(row.occursOn ?? row.date, locale)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {description(row) || '—'}
                  {row.pending === true && (
                    <Tooltip title={t('cashflow.pending')}>
                      <Box
                        component="span"
                        role="img"
                        aria-label={t('cashflow.pending')}
                        sx={{ display: 'inline-flex' }}
                      >
                        <InfoIcon />
                      </Box>
                    </Tooltip>
                  )}
                  {exception && !xs && (
                    <Box
                      component="span"
                      role="img"
                      aria-label={t('cashflow.exception')}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: tokens.warningFg,
                        display: 'inline-block',
                      }}
                    />
                  )}
                </Box>
              </TableCell>
              {!xs && (
                <TableCell align="right">
                  <MoneyText minor={row.amountMinor} currency={currency} tone="register" />
                </TableCell>
              )}
              <TableCell align="right">
                <MoneyText minor={running} currency={currency} tone="running" />
              </TableCell>
              {!xs && (
                <TableCell>
                  {row.state !== null && (
                    <StatusChip
                      domain="occurrence"
                      status={row.state}
                      values={occurrenceLabelValues(row.amountMinor)}
                    />
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
